import { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { useFieldDependencies } from "@/hooks/useFieldDependencies";
import { MetaFieldEditModal } from "../../../MetaFieldEditModal";
import type { ShopifySectionWidgetProps } from "../types";
import { useDynamicView } from "@/contexts/DynamicViewContext";
import { useResolveFieldLabel } from "@/lib/views/labels";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation, getUITranslation } from "@/lib/views/labels/translations";
import { deriveFieldGroupsFromOrderedFields, type FieldGroup } from "@/lib/views/utils/deriveFieldGroups";
import { ChildTablePreview } from "@/components/child-table/ChildTablePreview";
import { SectionHeader } from "../../../SectionHeader";
import { useDetailDensity } from "../../../../hooks/useDetailDensity";
import { useDetailEditMode } from "../../../../hooks/useDetailEditMode";
import { useInlineFieldSave } from "@/components/inline-edit/detail/useInlineFieldSave";
import { useBufferFieldSave } from "@/components/inline-edit/detail/useBufferFieldSave";
import { useInlineFieldUndo } from "@/components/inline-edit/detail/useInlineFieldUndo";
import { getAdjacentField } from "@/components/inline-edit/detail/getAdjacentField";
import { InlineFieldRow } from "../../../InlineFieldRow";
import { itemQueryKeys } from "@/features/inventory/api/itemQueryKeys";
import type { DocFieldSchema } from "@/lib/erp/meta";
import { CARD } from "@/config/design-tokens";

/**
 * Fields owned by InventorySection when used as a widget section (no schema columns).
 * Self-sourcing pattern: filters from metaFields at runtime.
 */
const INVENTORY_FIELDS = ["is_stock_item", "has_batch_no", "has_serial_no", "is_purchase_item", "is_sales_item"];

export function InventorySection({
  section,
  formData,
  onFieldChange,
  disabled,
  onSave,
  onSubmit,
  isSaving,
  metaFields,
  fieldErrors,
  onClearFieldError,
}: ShopifySectionWidgetProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [modalField, setModalField] = useState<DocFieldSchema | null>(null);
  const { textSize, rowPx, rowPy, rowFirst, rowGap, dividerMx, rowLabel } = useDetailDensity();

  // Edit mode: mobile forces modal (built into hook)
  const { editMode } = useDetailEditMode();
  const isNew = !formData?.name;

  // Inline save: network patch for existing docs, local buffer for new docs
  const inlineSave = useInlineFieldSave({
    doctype: "Item",
    docname: formData.name ?? "",
    detailKey: itemQueryKeys.detail(formData.name ?? ""),
  });
  const bufferSave = useBufferFieldSave();
  const { saveField, getFieldState, retryField, undoField, getUndoable, lastSavedField } = isNew ? bufferSave : inlineSave;

  useInlineFieldUndo({ undoField, getUndoable, lastSavedField });

  // Get view context for label resolution
  const { doctype, mode, style, enableSystemTranslations } = useDynamicView();
  const resolveLabel = useResolveFieldLabel();
  const { language } = useLanguage();

  // Get all fields from section columns, or self-source from metaFields for widget sections
  const allFields = useMemo(() => {
    const fromColumns = section.columns.flatMap((col) => col.fields);
    if (fromColumns.length > 0) return fromColumns;
    if (metaFields) {
      const selfSourced = metaFields.filter((f: any) => INVENTORY_FIELDS.includes(f.fieldname));
      return selfSourced;
    }
    return [];
  }, [section.columns, metaFields]);

  // Compute field dependencies
  const { visibleFieldNames, fieldRequired } = useFieldDependencies(allFields, formData);

  // Filter visible fields, exclude 'name' field
  const filteredFields = allFields
    .filter((field) => visibleFieldNames.has(field.fieldname))
    .filter((field) => field.fieldname !== "name");

  // Translate section label
  const translatedSectionLabel = useMemo(() => {
    return section.label ? getSectionTitleTranslation(section.label, language) : null;
  }, [section.label, language]);

  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      general: getUI("general", "General"),
      editFields: getUI("modal_edit_fields", "Edit Fields"),
    };
  }, [language]);

  const groups = useMemo(() => {
    if (filteredFields.length === 0) return [];
    if (metaFields && metaFields.length > 0) {
      return deriveFieldGroupsFromOrderedFields({
        metaFields,
        orderedFields: filteredFields,
        defaultGroupLabel: uiLabels.general,
      });
    }
    return [{
      key: "single",
      label: translatedSectionLabel || uiLabels.editFields,
      fields: filteredFields,
    }] satisfies FieldGroup[];
  }, [metaFields, filteredFields, translatedSectionLabel, uiLabels.general, uiLabels.editFields]);

  if (groups.length === 0) return null;

  const allFlatFields = groups.flatMap((g) => g.fields);
  const currency = formData.currency || "THB";

  const handleInlineCommit = async (fieldname: string, newValue: unknown) => {
    const previousValue = formData[fieldname];
    setEditingField(null);
    onFieldChange(fieldname, newValue);
    await saveField(fieldname, newValue, previousValue);
  };

  const handleAdvance = useCallback((fieldname: string, direction: "next" | "prev") => {
    const next = getAdjacentField(allFlatFields, fieldname, direction);
    if (next) setEditingField(next);
  }, [allFlatFields]);

  return (
    <>
      <div>
        {translatedSectionLabel && <SectionHeader label={translatedSectionLabel} textSize={textSize} />}

        <div
          className={cn(
            CARD,
            editMode === "modal" && !disabled && "cursor-pointer",
            disabled && "opacity-60 cursor-not-allowed"
          )}
          onClick={
            editMode === "modal"
              ? () => { if (!disabled) setModalOpen(true); }
              : undefined
          }
        >
          <div className="space-y-0">
            {allFlatFields.map((field, index) => {
              const value = formData[field.fieldname];
              const isTable = field.fieldtype === "Table" || field.fieldtype === "Table MultiSelect";
              const isRequired = fieldRequired[field.fieldname] ?? field.reqd === 1;
              const resolvedLabel = resolveLabel(doctype, field, mode, style, enableSystemTranslations);
              const isFirst = index === 0;
              const isLast = index === allFlatFields.length - 1;

              return (
                <InlineFieldRow
                  key={field.fieldname}
                  field={field}
                  value={value}
                  label={resolvedLabel.label}
                  editMode={editMode}
                  isEditing={editingField === field.fieldname}
                  onStartEdit={() => setEditingField(field.fieldname)}
                  onCommit={(v) => handleInlineCommit(field.fieldname, v)}
                  onCancel={() => setEditingField(null)}
                  saveState={getFieldState(field.fieldname)}
                  currency={currency}
                  tooltip={field.description}
                  fullWidth={isTable}
                  onModalClick={isTable ? () => setModalField(field) : undefined}
                  onAdvance={(dir) => handleAdvance(field.fieldname, dir)}
                  onRetry={() => retryField(field.fieldname)}
                  error={fieldErrors?.[field.fieldname]}
                  onClearError={() => onClearFieldError?.(field.fieldname)}
                  rowPx={rowPx}
                  rowPy={isFirst ? rowFirst : rowPy}
                  rowGap={rowGap}
                  rowLabel={rowLabel}
                  dividerMx={dividerMx}
                  isLast={isLast}
                  isRequired={isRequired}
                >
                  {isTable && (
                    <ChildTablePreview
                      field={field}
                      value={value}
                      currency={currency}
                      maxRows={3}
                      maxFields={3}
                    />
                  )}
                </InlineFieldRow>
              );
            })}
          </div>
        </div>
      </div>

      {/* Card-level modal (modal mode) */}
      <MetaFieldEditModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={translatedSectionLabel || section.label || "Inventory Settings"}
        fields={allFlatFields}
        section={undefined}
        formData={formData}
        onFieldChange={onFieldChange}
        onSubmit={onSubmit}
        onSave={onSave}
        disabled={disabled}
        isSaving={isSaving}
        documentName={formData?.name}
      />

      {/* Per-field modal for Table fields in inline mode */}
      {modalField && (
        <MetaFieldEditModal
          open={true}
          onOpenChange={(open) => { if (!open) setModalField(null); }}
          title={resolveLabel(doctype, modalField, mode, style, enableSystemTranslations).label}
          fields={[modalField]}
          section={undefined}
          formData={formData}
          onFieldChange={onFieldChange}
          onSubmit={onSubmit}
          onSave={onSave}
          disabled={disabled}
          isSaving={isSaving}
          documentName={formData?.name}
        />
      )}
    </>
  );
}
